import { useNavigate } from 'react-router-dom';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SettingsIcon from '@mui/icons-material/Settings';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { usePosSession } from '@/hooks/usePosSession';
import { useAppSelector } from '@/hooks/useAppSelector';
import { PosShiftDashboard } from './PosShiftDashboard';

export function PosShiftPage() {
  const navigate = useNavigate();
  const store = useAppSelector((s) => s.app.selectedStore);
  const { counterId, counters, loading, setCounterId } = usePosSession();

  if (!store) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center overflow-y-auto p-6">
        <EmptyState
          title="Select a store first"
          description="Choose your store from the POS dashboard, then return here to open or close a shift."
          actionLabel="Go to dashboard"
          onAction={() => navigate('/pos/dashboard')}
        />
      </div>
    );
  }

  if (!loading && counters.length === 0) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center overflow-y-auto p-6">
        <EmptyState
          icon={<SettingsIcon sx={{ fontSize: 40 }} />}
          title="No billing counters yet"
          description={`Create at least one billing counter for ${store.name} in POS Setup. Each counter represents a register or checkout lane.`}
          actionLabel="Open POS Setup"
          onAction={() => navigate('/pos/setup')}
          secondaryActionLabel="Back to billing"
          onSecondaryAction={() => navigate('/pos')}
        />
      </div>
    );
  }

  if (!counterId) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center overflow-y-auto p-6">
        <div className="w-full max-w-md space-y-5 text-center">
          <EmptyState
            icon={<PointOfSaleIcon sx={{ fontSize: 40 }} />}
            title="Select a billing counter"
            description="Use the Counter dropdown in the top bar (next to your name), then come back to open or close your shift."
            compact
          />
          {counters.length === 1 && (
            <Button type="button" onClick={() => setCounterId(counters[0].id)}>
              Use {counters[0].name} ({counters[0].code})
            </Button>
          )}
          {counters.length > 1 && (
            <div className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-4 text-left dark:border-[rgb(var(--border-subtle))]">
              <p className="mb-3 text-sm font-medium text-[rgb(var(--foreground))]">Available counters</p>
              <ul className="space-y-2">
                {counters.map((counter) => (
                  <li key={counter.id}>
                    <button
                      type="button"
                      className="w-full rounded-lg border border-[rgb(var(--border))] px-3 py-2 text-left text-sm transition hover:border-primary-300 hover:bg-primary-50 dark:border-[rgb(var(--border-subtle))] dark:hover:bg-primary-950/30"
                      onClick={() => setCounterId(counter.id)}
                    >
                      {counter.name} <span className="text-[rgb(var(--muted-foreground))]">({counter.code})</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <PosShiftDashboard counterId={counterId} />
    </div>
  );
}
